import { RoleTypes } from '@/portainer/rbac/models/role';

import {
  KubernetesRbacFormValues,
  KubernetesRbacPolicyPayload,
  KubernetesAccessPolicyValue,
} from './types';

// Namespace role IDs (Standard=3, Read-only=4, Namespace Operator=6)
const NAMESPACE_ROLE_IDS = [
  RoleTypes.STANDARD,
  RoleTypes.READ_ONLY,
  RoleTypes.NAMESPACE_OPERATOR,
] as const;

interface KubernetesAccessEntry {
  Id: number;
  Type: 'user' | 'team';
  Role: { Id: number };
  namespaces?: string[];
}

function isNamespaceRole(
  roleId: number
): roleId is (typeof NAMESPACE_ROLE_IDS)[number] {
  return NAMESPACE_ROLE_IDS.includes(
    roleId as (typeof NAMESPACE_ROLE_IDS)[number]
  );
}

/**
 * Builds the API payload from form data
 * All users/teams go into EnvironmentAccessPolicies
 * Namespace-scoped roles include Namespaces in their policy value
 */
export function buildKubernetesRBACPayload(
  formData: KubernetesRbacFormValues
): KubernetesRbacPolicyPayload {
  const accessList = (formData.accessList as KubernetesAccessEntry[]) || [];

  return {
    EnvironmentAccessPolicies: {
      UserAccessPolicies: buildAccessPolicies(
        accessList.filter((access) => access.Type === 'user')
      ),
      TeamAccessPolicies: buildAccessPolicies(
        accessList.filter((access) => access.Type === 'team')
      ),
    },
  };
}

function buildAccessPolicies(entries: KubernetesAccessEntry[]) {
  return Object.fromEntries(
    entries.map((entry) => {
      const policyValue: KubernetesAccessPolicyValue = {
        RoleId: entry.Role.Id,
      };

      if (isNamespaceRole(entry.Role.Id) && entry.namespaces) {
        policyValue.Namespaces = entry.namespaces;
      }

      return [
        entry.Id.toString(),
        {
          value: policyValue,
          enforce: false,
        },
      ];
    })
  );
}
